import {
  commitLocalUpdate,
  createOperationDescriptor,
  Environment,
  getRequest,
  graphql,
  Network,
  Observable,
  RecordSource,
  Store,
} from "relay-runtime";
import { SubscriptionClient } from "subscriptions-transport-ws";

import {
  getCSRFAccessToken,
  graphqlFetcher,
  requestWithAuth,
} from "@skydio/api_util/src/backends/cloud_api/requests-browser";
import { FetchError } from "@skydio/api_util/src/common/http";
import { CloudErrorCode } from "@skydio/pbtypes/pbtypes/gen/cloud_api/cloud_error_code_pb";
import { toast } from "@skydio/rivet-ui";

import { CLOUD_API_URL, CLOUD_API_WS_URL, IS_DEV } from "app/env";
import { trackAnalytic } from "utils/trackEvent";

import type { GraphQLResponse, RequestParameters, Subscribable, Variables } from "relay-runtime";

// Establish a graphql over websocket client connection based on the
// "graphql-ws" websocket subprotocol defined here:
//
// https://github.com/apollographql/subscriptions-transport-ws/blob/master/PROTOCOL.md
//
// Client implementation of the above protocol is provided by the
// "subscriptions-transport-ws" library
// (https://github.com/apollographql/subscriptions-transport-ws). This library
// is self-described as no longer maintained and recommends using the
// "graphql-ws" library (https://github.com/enisdenjo/graphql-ws) instead.
// However, and this is confusing, the "graphql-ws" client library implements a
// different graphql over websockets protocol documented here
// (https://github.com/enisdenjo/graphql-ws/blob/master/PROTOCOL.md) under the
// "graphql-transport-ws" subprotocol.
//
// The python backend readily available for Graphene and included in the
// graphql-python repository on github is also called "graphql-ws"
// (https://github.com/graphql-python/graphql-ws). The server does implement the
// "graphql-ws" subprotocol, but is not compatible with the "graphql-ws" client
// library mentioned above.
//
// I would prefer to use the new "graphql-transport-ws" subprotocol, but haven't
// found a server library for Graphene and I don't want to re-implement myself.
// So I'm sticking with this for now.

// Periodically check for inactivity every 15 seconds
// This is the time at which we will reconnect the subscription client if we haven't received any messages
const PERIODIC_RECONNECT_INTERVAL_MS = 15000; // Milliseconds
// Interval of time at which we want the server to send us heartbeats
const PING_PONG_INTERVAL_S = 3; // Seconds

// WeakMap to track connection health per client instance
// This ensures each client (including iframes) has its own independent state
interface ClientState {
  hasReceivedMessage: boolean;
  reconnectIntervalId: NodeJS.Timeout | null;
  pingSubscription: { unsubscribe: () => void } | null;
}

const clientStates = new Map<SubscriptionClient, ClientState>();

// Function to handle reconnection logic
const handleReconnection = (subscriptionClient: SubscriptionClient) => {
  const state = clientStates.get(subscriptionClient);
  if (!state) return;

  if (!state.hasReceivedMessage) {
    trackAnalytic("jwtEnvironment.handleReconnection.reconnect");
    // isForced is set to False which means we will reconnect
    if (getCSRFAccessToken()) {
      trackAnalytic("jwtEnvironment.handleReconnection.truthyGetCSRFAccessToken");
      subscriptionClient.close(false, true);
    } else {
      trackAnalytic("jwtEnvironment.handleReconnection.falsyGetCSRFAccessToken");
    }
  } else {
    trackAnalytic("jwtEnvironment.handleReconnection.noReconnectNeeded");
  }
  state.hasReceivedMessage = false;
};

const ensureSubscriptionClientConnected = (subscriptionClient: SubscriptionClient) => {
  // Don't check the websocket if we aren't authenticated, this will just result in an
  // server/authentiation error trying to establish the websocket.  We use the presence of
  // the CSRF access token as a proxy for being authenticated.
  if (!getCSRFAccessToken()) {
    return () => {
      // No-op cleanup function when not authenticated
    };
  }

  // Initialize or get state for this client
  let state = clientStates.get(subscriptionClient);
  if (!state) {
    state = {
      hasReceivedMessage: false,
      reconnectIntervalId: null,
      pingSubscription: null,
    };
    clientStates.set(subscriptionClient, state);
  }

  // Clean up any existing subscription/interval for this specific client
  if (state.pingSubscription) {
    state.pingSubscription.unsubscribe();
    state.pingSubscription = null;
  }
  if (state.reconnectIntervalId) {
    clearInterval(state.reconnectIntervalId);
    state.reconnectIntervalId = null;
  }

  // Set up a continuous ping-pong subscription that fires a message every PING_PONG_INTERVAL_S
  // Unlike the original implementation, we don't unsubscribe after the first response
  const observable = subscriptionClient.request({
    query: `subscription ping($intervalSeconds: Int) { time(intervalSeconds: $intervalSeconds) }`,
    operationName: "ping",
    variables: { intervalSeconds: PING_PONG_INTERVAL_S },
  });

  trackAnalytic("jwtEnvironment.ensureSubscriptionClientConnected.pingPongRequested");

  const subscription = observable.subscribe({
    next: response => {
      // PONG!  This is the expected response if we are connected!
      if (response.data?.time) {
        const clientState = clientStates.get(subscriptionClient);
        if (clientState) {
          clientState.hasReceivedMessage = true;
        }
      }
    },
    error: error => {
      trackAnalytic("jwtEnvironment.ensureSubscriptionClientConnected.pingPongError", {
        error: String(error),
      });
    },
  });

  // Store the subscription in the client's state
  state.pingSubscription = subscription;

  // Set up periodic reconnection check every PERIODIC_RECONNECT_INTERVAL_MS
  state.reconnectIntervalId = setInterval(() => {
    handleReconnection(subscriptionClient);
  }, PERIODIC_RECONNECT_INTERVAL_MS);

  // Return cleanup function
  return () => {
    const clientState = clientStates.get(subscriptionClient);
    trackAnalytic("jwtEnvironment.ensureSubscriptionClientConnected.cleanup");
    if (clientState) {
      // Clean up subscription
      if (clientState.pingSubscription) {
        clientState.pingSubscription.unsubscribe();
        clientState.pingSubscription = null;
      }
      // Clean up interval
      if (clientState.reconnectIntervalId) {
        clearInterval(clientState.reconnectIntervalId);
        clientState.reconnectIntervalId = null;
      }
      // Remove from Map
      clientStates.delete(subscriptionClient);
    } else {
      trackAnalytic("jwtEnvironment.ensureSubscriptionClientConnected.cleanup.noState");
    }
  };
};

// graphql-ws subprotocol client
export const makeSubscriptionClient = (onJwtExpired: () => void) => {
  const client = new SubscriptionClient(
    `${CLOUD_API_WS_URL}/subscriptions`,
    {
      reconnect: true,
      lazy: true,
      connectionParams: () => ({
        "X-CSRF-TOKEN": getCSRFAccessToken(),
      }),
      connectionCallback: (error: Error[]) => {
        // Assume that a connection error is from an expired JWT. This callback should trigger a
        // token refresh if one is necessary, and should only do this if we were already authenticated
        // to avoid repeatedly calling this before login
        if (error) {
          trackAnalytic("jwtEnvironment.connectionCallback.error", {
            error,
          });
          onJwtExpired();
        } else {
          trackAnalytic("jwtEnvironment.connectionCallback.success");
        }
      },
    }
    // NOTE(sam): We may need to revisit the websocket implementation here once we start doing
    // server-side rendering
  );

  const cleanup = ensureSubscriptionClientConnected(client);

  return { client, cleanup };
};

interface Query {
  operationName: string;
  variables: Variables;
}

interface JWTEnvironmentArgs {
  subscriptionClient: SubscriptionClient;
  // This callback should reset the authentication state and bounce the user back to login
  onJwtExpired: () => void;
}

export default function makeEnvironment({ subscriptionClient, onJwtExpired }: JWTEnvironmentArgs) {
  let environment: Environment | undefined = undefined;

  async function fetchQuery(operation: RequestParameters, variables: Variables) {
    try {
      const response = await requestWithAuth(
        `${CLOUD_API_URL}/graphql`,
        {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: operation.text,
            variables,
            operationName: operation.name,
          }),
        },
        graphqlFetcher
      );
      // In dev mode, send graphql errors prominently to the UI
      if (IS_DEV && response.errors) {
        response.errors.forEach((error: { message: string; path?: string[] }) => {
          toast.error(`${error.message}\n${(error.path ?? []).join(".")}`, {
            duration: 5000,
          });
        });
      }
      if (environment) {
        logQuery(environment, {
          variables,
          operationName: operation.name,
        });
      }
      return response;
    } catch (error) {
      if (error instanceof FetchError && error.code) {
        const errorcode = parseInt(error.code);
        if (
          errorcode === CloudErrorCode.Enum.REFRESH_REQUIRED ||
          errorcode === CloudErrorCode.Enum.UNAUTHENTICATED
        ) {
          // If this error has propagated up here it means the refresh token has expired
          // or the token is not valid (bad CSRF or similar).
          // Given the requestWithAuth fetcher will have already exhausted attempts to refresh
          // the token through normal means, we can assume the JWT is expired or broken.
          // The handler should log us out cleanly.
          onJwtExpired();
          // return an innocuous empty response to avoid the ErrorBoundary unmounting everything
          // loudly before we redirect; to my understanding, relay store caches based on ID keys
          // in the response, so this won't poison cache anywhere.
          return { data: {} };
        }
      }
      // Fallback for any other error (non-FetchError, FetchError without a code,
      // AbortError from canceled requests, JSON parse errors, etc.).
      // Relay's OperationExecutor will crash with
      // `TypeError: Cannot read properties of undefined (reading 'data')`
      // if fetchQuery ever resolves to undefined, so we must always return a
      // valid GraphQLResponse shape.
      return {
        errors: [{ message: String(error) }],
      };
    }
  }

  function subscribe(request: RequestParameters, variables: Variables) {
    const subscribeObservable = subscriptionClient.request({
      query: request.text || undefined,
      variables,
      operationName: request.name,
    });

    let subscriptionId = undefined;
    if (environment) {
      subscriptionId = addSubscription(environment, request.name);
    }
    // Important: Convert subscriptions-transport-ws observable type to Relay's
    return Observable.from(subscribeObservable as Subscribable<GraphQLResponse>).do({
      next: () => {
        // Mark that we've received a message from any subscription
        const state = clientStates.get(subscriptionClient);
        if (state) {
          state.hasReceivedMessage = true;
        }
        if (environment && subscriptionId) incrementUpdateCount(environment, subscriptionId);
      },
      unsubscribe: () => {
        if (environment && subscriptionId) removeSubscription(environment, subscriptionId);
      },
    });
  }

  environment = new Environment({
    network: Network.create(fetchQuery, subscribe),
    store: new Store(new RecordSource(), { queryCacheExpirationTime: 5 * 60 * 1000 }),
  });

  // Intialize client-only schema
  commitLocalUpdate(environment, store => {
    const root = store.getRoot();
    const relay = store.create("client:root:relay", "Relay");
    root.setLinkedRecord(relay, "relay");
    relay.setLinkedRecords([], "subscriptions");
    relay.setLinkedRecords([], "queries");
  });

  // Tell Relay to retain this data so it's not garbage collected
  const request = getRequest(graphql`
    query jwtEnvironment_LocalDataQuery @team(name: "cloud-infrastructure") {
      relay {
        # eslint-disable-next-line relay/unused-fields
        subscriptions {
          __typename
        }
        # eslint-disable-next-line relay/unused-fields
        queries {
          __typename
        }
      }
    }
  `);
  environment.retain(createOperationDescriptor(request, {}));

  return environment;
}

let subscriptionId = 0;
let queryId = 0;

function addSubscription(environment: Environment, name: string) {
  const id = `client:Subscription:${subscriptionId++}`;

  commitLocalUpdate(environment, store => {
    const relay = store.getRoot().getLinkedRecord("relay");
    if (!relay) {
      return;
    }
    const subscriptions = relay.getLinkedRecords("subscriptions") || [];

    //Create a new note record.
    const newSubscription = store.create(id, "Subscription");
    newSubscription.setValue(name, "name");
    newSubscription.setValue(0, "updateCount");

    // Add the record to the user's list of notes.
    relay.setLinkedRecords([...subscriptions, newSubscription], "subscriptions");
  });

  return id;
}

function incrementUpdateCount(environment: Environment, id: string) {
  commitLocalUpdate(environment, store => {
    const subscription = store.get(id);
    if (!subscription) {
      return;
    }
    const currentUpdateCount = subscription.getValue("updateCount") as number;
    subscription.setValue(currentUpdateCount + 1, "updateCount");
  });
}

function removeSubscription(environment: Environment, id: string) {
  commitLocalUpdate(environment, store => {
    const relay = store.getRoot().getLinkedRecord("relay");
    if (!relay) {
      return;
    }
    const subscriptions = relay.getLinkedRecords("subscriptions") || [];
    const newSubscriptions = subscriptions.filter(x => x.getDataID() !== id);
    store.delete(id);
    relay.setLinkedRecords(newSubscriptions, "subscriptions");
  });
}

interface Query {
  operationName: string;
  variables: Variables;
}

function logQuery(environment: Environment, query: Query) {
  const id = `client:RelayQuery:${queryId++}`;

  commitLocalUpdate(environment, store => {
    const relay = store.getRoot().getLinkedRecord("relay");
    if (!relay) {
      return;
    }
    const queries = relay.getLinkedRecords("queries") || [];

    //Create a new query record.
    const newQuery = store.create(id, "RelayQuery");
    newQuery.setValue(query.operationName, "operationName");
    newQuery.setValue(JSON.stringify(query.variables), "variables");

    // Add the record
    relay.setLinkedRecords([...queries, newQuery], "queries");
  });

  return id;
}
